import { useEffect, useMemo, useState } from "react";
import { api } from "./adminApi";

const API_ROOT =
  (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.trim()) ||
  (window.location.hostname === "localhost" ? "http://localhost:5000" : window.location.origin);


function toAbs(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return u.startsWith("/") ? `${API_ROOT}${u}` : `${API_ROOT}/${u}`;
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const onChange = () => setMatches(m.matches);
    m.addEventListener ? m.addEventListener("change", onChange) : m.addListener(onChange);
    return () => (m.removeEventListener ? m.removeEventListener("change", onChange) : m.removeListener(onChange));
  }, [query]);
  return matches;
}

export default function AdminPersons() {
  const isSmall = useMediaQuery("(max-width: 980px)");

  const [rows, setRows] = useState([]);
  // Pagination (client-side)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // create form
  const [nameEn, setNameEn] = useState("");
  const [nameKn, setNameKn] = useState("");
  const [slug, setSlug] = useState("");
  const [published, setPublished] = useState(true);
  const [createPhoto, setCreatePhoto] = useState(null);

  const total = rows.length;
  const publishedCount = useMemo(() => rows.filter((p) => !!p.is_published).length, [rows]);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const res = await api.get("/api/admin/persons");
      setRows(res.data || []);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load persons");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const pageCount = useMemo(() => Math.max(1, Math.ceil(rows.length / pageSize)), [rows.length, pageSize]);
  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return (rows || []).slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
    if (page < 1) setPage(1);
  }, [page, pageCount]);

  async function createPerson(e) {
    e.preventDefault();
    setErr("");
    try {
      const res = await api.post("/api/admin/persons", {
        name_en: nameEn,
        name_kn: nameKn,
        slug,
        is_published: published,
      });

      const newId = res?.data?.id;
      if (newId && createPhoto) {
        const fd = new FormData();
        fd.append("photo", createPhoto);
        await api.post(`/api/admin/persons/${newId}/photo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      setNameEn("");
      setNameKn("");
      setSlug("");
      setPublished(true);
      setCreatePhoto(null);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.message || "Create failed");
    }
  }

  async function uploadPhoto(personId, file) {
    if (!personId || !file) return;
    try {
      const fd = new FormData();
      fd.append("photo", file);
      await api.post(`/api/admin/persons/${personId}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
    } catch (e) {
      alert(e?.response?.data?.message || "Photo upload failed");
    }
  }

  async function togglePublish(p) {
    try {
      await api.patch(`/api/admin/persons/${p.id}`, { is_published: !p.is_published });
      await load();
    } catch (e) {
      alert(e?.response?.data?.message || "Update failed");
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <Header
          title="Manage Profiles"
          subtitle="Create profiles and control publish visibility. Upload profile photo and it will appear on the public page."
          right={
            <button onClick={load} style={styles.ghostBtn} title="Refresh">
              ⟳ Refresh
            </button>
          }
        />

        <div style={{ ...styles.grid, gridTemplateColumns: isSmall ? "1fr" : "420px 1fr" }}>
          {/* Create */}
          <Card
            title="Create Person"
            subtitle="Add a new biography profile."
            rightPill={<Pill tone="info">{published ? "Default: Published" : "Default: Hidden"}</Pill>}
          >
            <form onSubmit={createPerson} style={styles.form}>
              <Field label="Name (English)">
                <input
                  value={nameEn}
                  onChange={(e) => setNameEn(e.target.value)}
                  placeholder="Dr A P J Abdul Kalam"
                  style={styles.input}
                  required
                />
              </Field>

              <Field label="Name (Kannada)">
                <input
                  value={nameKn}
                  onChange={(e) => setNameKn(e.target.value)}
                  placeholder="ಕನ್ನಡ ಹೆಸರು"
                  style={styles.input}
                />
              </Field>

              <Field label="Slug">
                <input
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="apj-abdul-kalam"
                  style={styles.input}
                  required
                />
              </Field>

              <Field label="Profile Photo (Upload)">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setCreatePhoto((e.target.files && e.target.files[0]) || null)}
                  style={styles.input}
                />
                <div style={{ marginTop: 6, fontSize: 12, color: "#667085", fontWeight: 700 }}>
                  Recommended: square image (e.g., 600×600). Max size 2 MB.
                </div>
              </Field>

              <label style={styles.checkRow}>
                <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} />
                <span style={{ fontWeight: 800, color: "#111827" }}>Published</span>
                <span style={{ color: "#667085", fontWeight: 700, fontSize: 12 }}>
                  (If unchecked, profile stays hidden.)
                </span>
              </label>

              <button style={styles.primaryBtn} type="submit">
                Create Profile
              </button>

              {err ? <div style={styles.err}>{err}</div> : null}

              <div style={styles.smallMeta}>
                <span>
                  Total: <b>{total}</b>
                </span>
                <span>
                  Published: <b>{publishedCount}</b>
                </span>
              </div>
            </form>
          </Card>

          {/* List */}
          <Card
            title="Profiles List"
            subtitle="Upload photo, then toggle publish status anytime."
            rightPill={loading ? <Pill tone="warn">Loading…</Pill> : <Pill tone="success">Ready</Pill>}
          >
            {loading ? <div style={styles.loading}>Loading...</div> : null}

            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <Th>Name</Th>
                    <Th>Photo</Th>
                    <Th>Slug</Th>
                    <Th>Visibility</Th>
                    <Th>Action</Th>
                  </tr>
                </thead>

                <tbody>
                  {pagedRows.map((p) => (
                    <tr key={p.id} style={styles.tr}>
                      <Td>
                        <div style={styles.nameCell}>
                          <Avatar name={p.name_en || "P"} photoUrl={p.photo_url} />
                          <div style={{ minWidth: 0 }}>
                            <div style={styles.nameTitle}>{p.name_en || "-"}</div>
                            <div style={styles.nameSub}>{p.name_kn || ""}</div>
                          </div>
                        </div>
                      </Td>

                      <Td>
                        <div style={styles.photoCell}>
                          {p.photo_url ? (
                            <img alt="photo" src={toAbs(p.photo_url)} style={styles.photoThumb} />
                          ) : (
                            <div style={styles.noPhoto}>No photo</div>
                          )}

                          <label style={styles.uploadLabel}>
                            <input
                              type="file"
                              accept="image/*"
                              style={{ display: "none" }}
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                e.target.value = "";
                                if (f) uploadPhoto(p.id, f);
                              }}
                            />
                            Upload
                          </label>
                        </div>
                      </Td>

                      <Td>
                        <span style={styles.mono}>{p.slug}</span>
                      </Td>

                      <Td>
                        <span
                          style={{
                            ...styles.pill,
                            background: p.is_published ? "#ecfdf3" : "#f2f4f7",
                            color: p.is_published ? "#027a48" : "#344054",
                            borderColor: p.is_published ? "#abefc6" : "#e6eaf0",
                          }}
                        >
                          {p.is_published ? "Published" : "Hidden"}
                        </span>
                      </Td>

                      <Td>
                        <button
                          style={{
                            ...styles.actionBtn,
                            ...(p.is_published ? styles.dangerOutline : styles.primaryOutline),
                          }}
                          onClick={() => togglePublish(p)}
                        >
                          {p.is_published ? "Unpublish" : "Publish"}
                        </button>
                      </Td>
                    </tr>
                  ))}

                  {rows.length === 0 ? (
                    <tr>
                      <Td colSpan={5}>
                        <div style={styles.empty}>No profiles yet.</div>
                      </Td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

/* ---------- Small UI building blocks ---------- */

function PaginationBar({ page, pageSize, total, pageCount, onPageChange, onPageSizeChange, compact }) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);

  return (
    <div style={styles.pager}>
      <div style={styles.pagerLeft}>
        <span style={styles.pagerMeta}>Showing {from}-{to} of {total}</span>
      </div>

      <div style={styles.pagerRight}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button
            type="button"
            style={{ ...styles.pagerBtn, ...(page <= 1 ? styles.pagerBtnDisabled : null) }}
            onClick={() => onPageChange(1)}
            disabled={page <= 1}
            title="First"
          >
            «
          </button>
          <button
            type="button"
            style={{ ...styles.pagerBtn, ...(page <= 1 ? styles.pagerBtnDisabled : null) }}
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            title="Previous"
          >
            ‹
          </button>

          {!compact ? (
            <span style={styles.pagerMeta}>
              Page <b>{page}</b> / {pageCount}
            </span>
          ) : (
            <span style={styles.pagerMeta}>
              <b>{page}</b>/{pageCount}
            </span>
          )}

          <button
            type="button"
            style={{ ...styles.pagerBtn, ...(page >= pageCount ? styles.pagerBtnDisabled : null) }}
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pageCount}
            title="Next"
          >
            ›
          </button>
          <button
            type="button"
            style={{ ...styles.pagerBtn, ...(page >= pageCount ? styles.pagerBtnDisabled : null) }}
            onClick={() => onPageChange(pageCount)}
            disabled={page >= pageCount}
            title="Last"
          >
            »
          </button>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={styles.pagerMeta}>Rows</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            style={styles.pagerSelect}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>
    </div>
  );
}

function Header({ title, subtitle, right }) {
  return (
    <div style={styles.header}>
      <div style={{ minWidth: 0 }}>
        <div style={styles.hTitle}>{title}</div>
        <div style={styles.hSub}>{subtitle}</div>
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>{right}</div>
    </div>
  );
}

function Card({ title, subtitle, rightPill, children }) {
  return (
    <div style={styles.card}>
      <div style={styles.cardHead}>
        <div style={{ minWidth: 0 }}>
          <div style={styles.cardTitle}>{title}</div>
          <div style={styles.cardSub}>{subtitle}</div>
        </div>
        {rightPill}
      </div>
      <div style={styles.cardBody}>{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <div style={styles.label}>{label}</div>
      {children}
    </div>
  );
}

function Pill({ tone = "info", children }) {
  const map = {
    info: { bg: "#eff8ff", bd: "#b2ddff", tx: "#175cd3" },
    success: { bg: "#ecfdf3", bd: "#abefc6", tx: "#067647" },
    warn: { bg: "#fffaeb", bd: "#fedf89", tx: "#b54708" },
  };
  const t = map[tone] || map.info;
  return (
    <span style={{ ...styles.pill, background: t.bg, borderColor: t.bd, color: t.tx }}>
      {children}
    </span>
  );
}

function Avatar({ name, photoUrl }) {
  const initial = String(name || "P").trim().slice(0, 1).toUpperCase();
  return (
    <div style={styles.avatar} title={name}>
      {photoUrl ? (
        <img
          alt={name || "person"}
          src={toAbs(photoUrl)}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : (
        initial
      )}
    </div>
  );
}

function Th({ children }) {
  return <th style={styles.th}>{children}</th>;
}

function Td({ children, colSpan }) {
  return (
    <td colSpan={colSpan} style={styles.td}>
      {children}
    </td>
  );
}

/* ---------- Styles ---------- */

const styles = {
  page: {
    width: "100%",
    minHeight: "calc(100vh - 60px)",
  },
  container: {
    width: "100%",
    maxWidth: 1240,
    margin: "0 auto",
    padding: "16px 14px 28px",
  },

  header: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 12,
  },
  hTitle: { fontSize: 26, fontWeight: 950, color: "#101828", letterSpacing: "-0.4px" },
  hSub: { marginTop: 6, color: "#667085", fontWeight: 700, fontSize: 13, maxWidth: 760 },

  ghostBtn: {
    height: 38,
    padding: "0 12px",
    borderRadius: 12,
    border: "1px solid #e6eaf0",
    background: "#fff",
    fontWeight: 900,
    cursor: "pointer",
  },

  grid: {
    display: "grid",
    gap: 14,
    alignItems: "start",
  },

  card: {
    background: "#fff",
    border: "1px solid #e6eaf0",
    borderRadius: 18,
    boxShadow: "0 10px 30px rgba(16,24,40,0.06)",
    overflow: "hidden",
  },
  cardHead: {
    padding: 14,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    borderBottom: "1px solid #eef2f7",
    background: "linear-gradient(180deg,#ffffff,#fbfcfe)",
  },
  cardTitle: { fontSize: 16, fontWeight: 950, color: "#101828" },
  cardSub: { marginTop: 4, fontSize: 12, color: "#667085", fontWeight: 700 },
  cardBody: { padding: 14 },

  form: { display: "grid", gap: 10 },
  label: { fontSize: 12, fontWeight: 950, color: "#344054" },

  input: {
    height: 42,
    padding: "0 12px",
    borderRadius: 12,
    border: "1px solid #e6eaf0",
    outline: "none",
    fontSize: 14,
    fontWeight: 700,
  },

  checkRow: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    marginTop: 2,
    userSelect: "none",
  },

  primaryBtn: {
    height: 44,
    borderRadius: 14,
    border: 0,
    cursor: "pointer",
    fontWeight: 950,
    color: "#fff",
    background: "linear-gradient(135deg,#155eef,#0b4dd8)",
    boxShadow: "0 10px 20px rgba(21,94,239,0.25)",
  },

  err: {
    marginTop: 8,
    padding: 10,
    borderRadius: 12,
    border: "1px solid #fecdd3",
    background: "#fff1f3",
    color: "#b42318",
    fontWeight: 900,
    fontSize: 13,
  },

  smallMeta: {
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
    marginTop: 10,
    paddingTop: 10,
    borderTop: "1px dashed #eef2f7",
    color: "#667085",
    fontWeight: 800,
    fontSize: 13,
  },

  loading: { padding: 10, color: "#667085", fontWeight: 800 },

  tableWrap: {
    border: "1px solid #eef2f7",
    borderRadius: 14,
    overflow: "auto",
    maxHeight: 520,
  },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 740 },
  th: {
    textAlign: "left",
    padding: 12,
    borderBottom: "1px solid #e6eaf0",
    color: "#667085",
    fontSize: 12,
    fontWeight: 950,
    background: "#fbfcfe",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  td: {
    padding: 12,
    borderBottom: "1px solid #f2f4f7",
    fontSize: 14,
    verticalAlign: "top",
    color: "#0f172a",
  },
  tr: { transition: "background 140ms ease" },

  nameCell: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
  nameTitle: {
    fontWeight: 950,
    color: "#0f172a",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 320,
  },
  nameSub: { color: "#667085", fontWeight: 750, fontSize: 12 },

  mono: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSize: 13,
    fontWeight: 800,
    color: "#344054",
  },

  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 10px",
    borderRadius: 999,
    fontWeight: 950,
    fontSize: 12,
    border: "1px solid",
    textTransform: "capitalize",
    whiteSpace: "nowrap",
  },

  actionBtn: {
    height: 36,
    padding: "0 12px",
    borderRadius: 12,
    cursor: "pointer",
    fontWeight: 950,
    background: "#fff",
  },
  primaryOutline: { border: "1px solid #b2ddff", color: "#175cd3" },
  dangerOutline: { border: "1px solid #fecdd3", color: "#b42318" },

  avatar: {
    width: 34,
    height: 34,
    borderRadius: 999,
    background: "linear-gradient(135deg,#ffffff,#eef2ff)",
    border: "1px solid #e6eaf0",
    display: "grid",
    placeItems: "center",
    fontWeight: 950,
    color: "#1d4ed8",
    flex: "0 0 auto",
    overflow: "hidden",
  },

  photoCell: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    alignItems: "flex-start",
  },
  photoThumb: {
    width: 72,
    height: 52,
    borderRadius: 12,
    border: "1px solid #e6eaf0",
    objectFit: "cover",
    background: "#fff",
    display: "block",
  },
  noPhoto: {
    width: 72,
    height: 52,
    borderRadius: 12,
    border: "1px dashed #e6eaf0",
    background: "#fbfcfe",
    display: "grid",
    placeItems: "center",
    fontSize: 12,
    color: "#98a2b3",
    fontWeight: 900,
  },
  uploadLabel: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: 32,
    padding: "0 12px",
    borderRadius: 12,
    border: "1px solid #d0d5dd",
    background: "#fff",
    fontWeight: 950,
    fontSize: 12,
    color: "#344054",
    cursor: "pointer",
  },

  empty: {
    padding: 12,
    borderRadius: 14,
    background: "#f8fafc",
    border: "1px solid #eef2f7",
    fontWeight: 850,
    color: "#667085",
  },

pager: {
  marginTop: 12,
  paddingTop: 12,
  borderTop: "1px solid #eef2f7",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
},
pagerLeft: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
pagerRight: { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, flexWrap: "wrap" },
pagerMeta: { fontSize: 12, fontWeight: 800, color: "#667085" },
pagerBtn: {
  height: 34,
  width: 36,
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 950,
  color: "#0f172a",
},
pagerBtnDisabled: { opacity: 0.45, cursor: "not-allowed" },
pagerSelect: {
  height: 34,
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  padding: "0 10px",
  fontWeight: 900,
  background: "#fff",
  outline: "none",
},
};
